import { Alert, Anchor, Badge, Box, Button, Group, Modal, Paper, Skeleton, Stack, Switch, Table, Text, TextInput, Textarea, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { Code, ConnectError } from '@connectrpc/connect';
import { notifications } from '@mantine/notifications';
import type { LegalDocumentSummary, LegalVersion } from '@motionsafe/ms-console-api/admin/v1/legal_pb.js';
import { IconAlertTriangle, IconArchive, IconArrowLeft, IconFileOff, IconPencil, IconSend, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useId, useState, type FormEvent } from 'react';

import { api } from '../../api/client';
import { errorText, legalDocumentQuery, meQuery, useInvalidate } from '../../api/hooks';
import { confirmFirst } from '../../components/confirm';
import { useDialog, useShown } from '../../components/dialog';
import { useGridRows } from '../../components/grid';
import { LegalBlocks } from '../../components/legal-blocks';
import { LegalEditor, useLegalPreview } from '../../components/legal-editor';
import { LOCKED_INPUT, LOCKED_ORDER, locked, LockedSwitch } from '../../components/locked';
import { DrawerSection, EmptyState, Facts, PageHeader, ToolbarActions, type StatProps } from '../../components/page';
import { PaneBody, PaneDrawer, PaneHeader } from '../../components/pane';
import { day, localDay, when } from '../../lib/format';
import { fromWire, peopleFraction } from '../../lib/legal';
import { isStaff } from '../../lib/roles';
import { ICON, ICON_INLINE } from '../../theme';
import { useAccess, useOrgId } from '../_app';
import { DialogLoadBar } from '../../components/top-load-bar';

// One of MotionSafe's legal documents: what is in effect, what people must
// have accepted, its draft, and every version it has had. A MotionSafe admin
// writes the draft beside the device's own drawing of it, saves it, and
// publishes it as the next version, choosing whether it asks everyone to
// accept again and from when. A published version is fixed; a correction is
// a new version. The rest of staff read all of it, with the controls locked.
export const Route = createFileRoute('/_app/legal-documents_/$documentId')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery);
    if (!me || !isStaff(me)) throw redirect({ to: '/overview', search: {} });
  },
  component: LegalDocumentPage,
});

const HAIRLINE = '1px solid var(--mantine-color-default-border)';

function stats(doc: LegalDocumentSummary): StatProps[] {
  const v = doc.inEffect;
  return [
    { label: 'In effect', value: v ? `Version ${v.version}` : 'none', hint: v ? `Since ${day(v.effectiveAt ?? v.publishedAt)}` : doc.status === 'retired' ? 'Retired before a version took effect' : 'Nobody is asked until one is published', tone: v || doc.status === 'retired' ? 'neutral' : 'warn' },
    { label: 'Required', value: doc.requiredVersion ? `Version ${doc.requiredVersion}` : 'none', hint: doc.requiredVersion ? 'Accepting it or a later one meets it' : 'Nothing is asked yet' },
    { label: 'Scheduled', value: doc.scheduled ? `Version ${doc.scheduled.version}` : 'none', hint: doc.scheduled ? `In effect from ${day(doc.scheduled.effectiveAt)}` : 'Nothing published for a later date' },
    { label: 'Draft', value: doc.draft ? `Version ${doc.draft.version}` : 'none', hint: doc.draft ? `Saved ${when(doc.draft.updatedAt, { relative: true })}` : 'Seen by MotionSafe staff only' },
  ];
}

function LegalDocumentPage() {
  const { documentId } = Route.useParams();
  const orgId = useOrgId();
  const q = useQuery(legalDocumentQuery(documentId));
  const doc = q.data?.document;
  const versions = (q.data?.versions ?? []).filter((v) => v.status === 'published');
  const missing = q.error instanceof ConnectError && q.error.code === Code.NotFound;
  const back = (
    <Anchor size="sm" display="inline-block" mb="md" renderRoot={(p) => <Link {...p} to="/legal-documents" search={{ org: orgId }} />}>
      <Group component="span" gap={4} wrap="nowrap"><IconArrowLeft {...ICON_INLINE} aria-hidden="true" />Legal documents</Group>
    </Anchor>
  );

  if (missing) {
    return (
      <>
        {back}
        <Paper p={0}>
          <EmptyState icon={IconFileOff} title="No such document" body="It may have been created and removed in the database by hand, or the address is out of date. Every document is listed on Legal documents." />
        </Paper>
      </>
    );
  }

  return (
    <>
      {back}
      <PageHeader
        title={doc?.title ?? ' '}
        description={doc ? <Describe doc={doc} /> : ' '}
        stats={doc ? stats(doc) : 'loading'}
        statsCount={4}
      />
      {q.isError && (
        <Group gap="sm" mb="lg">
          <Text size="sm" c="danger">Could not load the document: {errorText(q.error)}.</Text>
          <Button variant="subtle" size="compact-sm" onClick={() => void q.refetch()} disabled={q.isFetching}>Try again</Button>
        </Group>
      )}
      {doc && <Retire doc={doc} />}
      {/* A new draft, a publish, or a discard starts the editor again from what is on record. */}
      {doc && doc.status === 'active' && <DraftCard key={`${doc.id}:${doc.draft?.id ?? 'none'}`} doc={doc} />}
      <Versions doc={doc} versions={versions} loading={q.isPending} />
    </>
  );
}

// What the document is doing now. It is not public: people read what they
// accepted under Your legal documents.
function Describe({ doc }: { doc: LegalDocumentSummary }) {
  if (doc.status === 'retired') return <>Retired: asked of no one. Its versions and everyone's acceptances stay on record.</>;
  if (!doc.inEffect) return <>Nobody is asked to accept it until its first version is published. Its key is <span className="id">{doc.key}</span>.</>;
  return <>Asked of everyone who signs in, unless their organization's custom agreement covers them. Its key is <span className="id">{doc.key}</span>.</>;
}

// Retiring and deleting, on the row above the draft. Retired: asked of no
// one from now on. Deleted: gone from the console, its key free again, its
// versions, acceptances and audit lines kept.
function Retire({ doc }: { doc: LegalDocumentSummary }) {
  const access = useAccess();
  const write = access.may('RetireLegalDocument');
  const captionId = useId();
  const inv = useInvalidate();
  const deleting = useDialog();
  const retire = useMutation({
    mutationFn: () => api.legal.retireLegalDocument({ documentId: doc.id }),
    onSuccess: () => { void inv('legal'); notifications.show({ message: `${doc.title} retired. Nobody is asked to accept it from now on.` }); },
    onError: (e) => notifications.show({ color: 'danger', message: errorText(e) }),
  });
  const ask = () => confirmFirst({
    title: `Retire ${doc.title}?`,
    body: 'Nobody is asked to accept it from now on. Its versions and everyone\'s acceptances stay readable here. A retired document cannot be brought back, and it keeps its key. Recorded in the staff audit trail under your name.',
    label: 'Retire document',
    danger: true,
    onConfirm: () => retire.mutateAsync(),
  });
  return (
    <Group align="center" gap="sm" mb="md" wrap="wrap">
      <Text size="sm" c="dimmed">A published version is fixed. A correction is a new version, and only a change that needs it asks everyone again.</Text>
      <ToolbarActions note={write ? undefined : access.caption('RetireLegalDocument')} noteId={captionId}>
        {doc.status === 'active' && (write
          ? <Button size="sm" variant="default" leftSection={<IconArchive {...ICON_INLINE} />} onClick={ask} disabled={retire.isPending}>Retire document</Button>
          : <Button size="sm" variant="default" leftSection={<IconArchive {...ICON_INLINE} />} {...locked(captionId)}>Retire document</Button>)}
        {write
          ? <Button size="sm" variant="default" color="danger" leftSection={<IconTrash {...ICON_INLINE} />} onClick={deleting.open}>Delete document</Button>
          : <Button size="sm" variant="default" leftSection={<IconTrash {...ICON_INLINE} />} {...locked(captionId)}>Delete document</Button>}
      </ToolbarActions>
      {write && deleting.mounted && <DeleteDocumentDialog opened={deleting.opened} round={deleting.round} onClose={deleting.close} doc={doc} />}
    </Group>
  );
}

function DeleteDocumentDialog({ opened, round, ...props }: { opened: boolean; round: number; onClose: () => void; doc: LegalDocumentSummary }) {
  return (
    <Modal opened={opened} onClose={props.onClose} title={`Delete ${props.doc.title}?`} size={440}>
      <DialogLoadBar />
      <DeleteDocumentForm key={round} {...props} />
    </Modal>
  );
}

// Confirmed by typing the key, as deleting an organization is by its slug,
// and laid out as that dialog is: what goes and what stays in one warning,
// the gentler way out named under it, and the key.
function DeleteDocumentForm({ onClose, doc }: { onClose: () => void; doc: LegalDocumentSummary }) {
  const inv = useInvalidate();
  const navigate = useNavigate();
  const orgId = useOrgId();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');
  const matches = typed.trim() === doc.key;
  const remove = useMutation({
    mutationFn: () => api.legal.deleteLegalDocument({ documentId: doc.id, confirmKey: typed.trim() }),
    onSuccess: () => {
      void inv('legal');
      notifications.show({ message: `${doc.title} deleted. Everyone's acceptances of it and the audit trail are kept.` });
      onClose();
      void navigate({ to: '/legal-documents', search: { org: orgId } });
    },
    onError: (e) => setError(errorText(e)),
  });
  const asked = doc.status === 'active' && !!doc.inEffect;
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (matches && !remove.isPending) remove.mutate(); }}>
      <Stack gap="lg">
        <Alert color="danger" role="alert" icon={<IconAlertTriangle {...ICON} />} title="This cannot be undone">
          <Text size="sm">
            {asked ? 'Nobody is asked to accept it from now on, and it' : 'It'} leaves the console for good: its page, every version and its text, and who accepted it on People. Everyone&rsquo;s acceptances and the audit trail stay in the database, where only MotionSafe engineering can read them.
          </Text>
        </Alert>
        {doc.status === 'active' && <Text size="sm" c="dimmed">Retiring stops anyone being asked and keeps all of it readable here. If that is enough, retire it instead.</Text>}
        <TextInput
          label="Key"
          withAsterisk
          description={<>Type <span className="id">{doc.key}</span> to confirm. The key can then be used for a new document.</>}
          value={typed}
          onChange={(e) => { setTyped(e.currentTarget.value); setError(''); }}
          error={error || undefined}
          autoComplete="off"
          spellCheck={false}
          data-autofocus
          styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
        />
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={remove.isPending}>Cancel</Button>
          <Button type="submit" color="danger" disabled={!matches || remove.isPending}>Delete document</Button>
        </Group>
      </Stack>
    </form>
  );
}

interface DraftFields { title: string; body: string; summary: string }

const fieldsOf = (v: LegalVersion | undefined, fallbackTitle: string): DraftFields => ({ title: v?.title ?? fallbackTitle, body: v?.bodyMarkdown ?? '', summary: v?.changeSummary ?? '' });
const sameFields = (a: DraftFields, b: DraftFields) => a.title === b.title && a.body === b.body && a.summary === b.summary;

// The next version, as it is being written. With no draft saved, it starts
// from the version in effect, and saving it makes the draft. Staff who are
// not admins read the draft, with every control locked.
function DraftCard({ doc }: { doc: LegalDocumentSummary }) {
  const access = useAccess();
  const write = access.may('SaveLegalDraft');
  const caption = access.caption('SaveLegalDraft');
  const captionId = useId();
  const inv = useInvalidate();
  const draft = doc.draft;
  // What is on record: the draft, else the version it starts from. The page
  // compares what is typed with it to know whether there is anything to save.
  const base = doc.scheduled ?? doc.inEffect;
  const saved = draft ? fieldsOf(draft, doc.title) : { ...fieldsOf(base, doc.title), summary: '' };
  const [fields, setFields] = useState<DraftFields>(saved);
  const set = (patch: Partial<DraftFields>) => { setFields((f) => ({ ...f, ...patch })); setError(''); };
  const [error, setError] = useState('');
  const preview = useLegalPreview(fields.body);
  const dirty = !sameFields(fields, saved);
  const nextVersion = draft?.version ?? Math.max(0, doc.inEffect?.version ?? 0, doc.scheduled?.version ?? 0) + 1;
  const first = !doc.inEffect && !doc.scheduled;
  const publishing = useDialog();
  // With no draft on record, the next version is begun on purpose, not left
  // open: just after a publish the page says there is none, rather than
  // showing the published text in an editor that reads as a new draft.
  const [started, setStarted] = useState(!!draft);
  // The draft the publish dialog is for: the one on record once Publish has saved what is typed.
  const [toPublish, setToPublish] = useState<LegalVersion | null>(null);

  // `forPublish`: saved on the way to publishing, which says so itself, so no toast of its own.
  const save = useMutation({
    mutationFn: (_how: { forPublish?: boolean } = {}) => api.legal.saveLegalDraft({ documentId: doc.id, title: fields.title.trim(), bodyMarkdown: fields.body, changeSummary: fields.summary.trim() }),
    onSuccess: (r, how) => {
      // What the service kept, trimmed as it keeps it, is what is on record now.
      if (r.draft) setFields(fieldsOf(r.draft, doc.title));
      void inv('legal');
      if (!how.forPublish) notifications.show({ message: `Draft of version ${r.draft?.version ?? nextVersion} saved. Nobody sees it until it is published.` });
    },
    onError: (e) => setError(errorText(e)),
  });
  const discard = useMutation({
    mutationFn: () => api.legal.deleteLegalDraft({ documentId: doc.id }),
    onSuccess: () => {
      void inv('legal');
      notifications.show({ message: 'Draft discarded. The version in effect is as it was.' });
    },
    onError: (e) => notifications.show({ color: 'danger', message: errorText(e) }),
  });
  const askDiscard = () => confirmFirst({
    title: `Discard the draft of version ${nextVersion}?`,
    body: 'Its title, text, and what changed are deleted. Nobody has seen it, and the version in effect stays as it is.',
    label: 'Discard draft',
    danger: true,
    onConfirm: () => discard.mutateAsync(),
  });

  // Why Publish cannot be used yet, and what to do about it. What is typed
  // and not saved is no reason: Publish saves it first.
  const publishNote = !fields.title.trim()
    ? 'Give it a title to publish it.'
    : !fields.body.trim()
      ? 'Write its text to publish it.'
      : preview.problem
        ? 'Fix the line the preview cannot draw to publish it.'
        : doc.scheduled
          ? `Version ${doc.scheduled.version} is scheduled for ${day(doc.scheduled.effectiveAt)}. Publish this one once it takes effect.`
          : '';
  // Publish saves what is typed, then asks how to publish it.
  const startPublish = async () => {
    if (dirty || !draft) {
      const r = await save.mutateAsync({ forPublish: true }).catch(() => null);
      if (!r?.draft) return;
      setToPublish(r.draft);
    } else {
      setToPublish(draft);
    }
    publishing.open();
  };
  const savedLine = draft
    ? `Seen by MotionSafe staff only. Saved ${when(draft.updatedAt)}${dirty ? '; there are changes not saved yet. Publish saves them first.' : '.'}`
    : base
      ? `Not saved yet. It starts from version ${base.version}. Save draft keeps it for later; Publish saves it and publishes it.`
      : 'Not saved yet. Save draft keeps it for later; Publish saves it and publishes it.';

  if (!draft && (!write || !started)) {
    return (
      <Paper p={0} mb="xl">
        <CardHead title="Draft" line="Seen by MotionSafe staff only." />
        <EmptyState
          compact
          icon={IconFileOff}
          title="No draft"
          body={write
            ? `${base ? `Version ${base.version} is ${doc.scheduled ? 'scheduled' : 'in effect'}. ` : ''}Start version ${nextVersion} when something needs to change; it begins from the text people read now.`
            : `A MotionSafe admin writes the next version here. ${caption}`}
          {...(write ? { action: <Button variant="default" leftSection={<IconPencil {...ICON_INLINE} />} onClick={() => setStarted(true)}>Start version {nextVersion}</Button> } : {})}
        />
      </Paper>
    );
  }

  return (
    <Paper p={0} mb="xl" component="section" aria-label={`Draft of version ${nextVersion}`}>
      <CardHead title={`Draft of version ${nextVersion}`} line={savedLine} />
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (write && dirty && !save.isPending) save.mutate({}); }}>
        <Stack gap="lg" p="lg">
          <TextInput
            label="Title"
            withAsterisk={write}
            value={fields.title}
            onChange={(e) => set({ title: e.currentTarget.value })}
            maw={560}
            {...(write ? {} : { ...LOCKED_INPUT, inputWrapperOrder: LOCKED_ORDER, description: caption })}
          />
          <Textarea
            label="What changed"
            description={first ? 'A first version has nothing to compare with; people who accepted an earlier version see this above the text.' : 'Shown above the text to people who accepted an earlier version, when this one asks them again. One or two sentences.'}
            value={fields.summary}
            onChange={(e) => set({ summary: e.currentTarget.value })}
            autosize
            minRows={2}
            maxRows={5}
            maw={720}
            {...(write ? {} : { ...LOCKED_INPUT, inputWrapperOrder: LOCKED_ORDER })}
          />
          <LegalEditor
            value={fields.body}
            onChange={(body) => set({ body })}
            preview={preview}
            title={fields.title.trim()}
            {...(write ? {} : { locked: caption })}
          />
          {error && <Text size="sm" c="danger" role="alert">{error}</Text>}
        </Stack>
        <Group justify="space-between" align="center" gap="sm" px="lg" py="md" wrap="wrap" style={{ borderTop: HAIRLINE }}>
          {write ? (
            <>
              <Group gap="sm">
                {draft && <Button variant="subtle" color="gray" onClick={askDiscard} disabled={save.isPending}>Discard draft</Button>}
              </Group>
              <Group gap="sm" justify="flex-end" wrap="wrap">
                {publishNote && <Text id={captionId} size="xs" c="dimmed" maw={320} ta="right">{publishNote}</Text>}
                <Button type="submit" variant="default" disabled={!dirty || !fields.title.trim() || save.isPending}>Save draft</Button>
                <Button leftSection={<IconSend {...ICON_INLINE} />} disabled={!!publishNote || save.isPending} aria-describedby={publishNote ? captionId : undefined} onClick={() => void startPublish()}>Publish</Button>
              </Group>
            </>
          ) : (
            <>
              <Text id={captionId} size="xs" c="dimmed">{caption}</Text>
              <Group gap="sm">
                <Button variant="default" {...locked(captionId)}>Save draft</Button>
                <Button leftSection={<IconSend {...ICON_INLINE} />} variant="default" {...locked(captionId)}>Publish</Button>
              </Group>
            </>
          )}
        </Group>
      </form>
      {write && toPublish && publishing.mounted && <PublishDialog opened={publishing.opened} round={publishing.round} onClose={publishing.close} doc={doc} draft={toPublish} />}
    </Paper>
  );
}

// A card's titled head: the title in a band of its own on the recessed
// surface, with one line saying what the card holds (DESIGN.web.md 7.6).
function CardHead({ title, line }: { title: string; line: string }) {
  return (
    <Box px="lg" py="md" style={{ background: 'var(--mantine-color-default-hover)', borderBottom: HAIRLINE, borderTopLeftRadius: 'var(--mantine-radius-md)', borderTopRightRadius: 'var(--mantine-radius-md)' }}>
      <Title order={2} fz="md" lh={1.4}>{title}</Title>
      <Text size="sm" c="dimmed">{line}</Text>
    </Box>
  );
}

function PublishDialog({ opened, round, ...props }: { opened: boolean; round: number; onClose: () => void; doc: LegalDocumentSummary; draft: LegalVersion }) {
  return (
    <Modal opened={opened} onClose={props.onClose} title={`Publish version ${props.draft.version}`} size={480}>
      <DialogLoadBar />
      <PublishForm key={round} {...props} />
    </Modal>
  );
}

// Publishing: whether it asks everyone again (always, for a first version),
// from when, and how many people that asks, as a fraction of everyone who
// signs in. The count is the service's, for the choice as it stands.
function PublishForm({ onClose, doc, draft }: { onClose: () => void; doc: LegalDocumentSummary; draft: LegalVersion }) {
  const inv = useInvalidate();
  const first = !doc.inEffect && !doc.scheduled;
  const [again, setAgain] = useState(true);
  const asksAgain = first || again;
  const [from, setFrom] = useState<string | null>(null);
  const [error, setError] = useState('');
  const count = useQuery({
    queryKey: ['legal-publish-preview', doc.id, asksAgain],
    queryFn: () => api.legal.previewLegalPublish({ documentId: doc.id, requiresReacceptance: asksAgain }),
  });
  const effective = from ? localDay(from, 'start') : undefined;
  const publish = useMutation({
    mutationFn: () => api.legal.publishLegalVersion({ documentId: doc.id, requiresReacceptance: asksAgain, ...(effective ? { effectiveAt: timestampFromDate(effective) } : {}) }),
    onSuccess: () => {
      void inv('legal');
      const when_ = effective ? ` It is asked for from ${effective.toLocaleDateString(undefined, { dateStyle: 'medium' })}, at each person's next sign-in.` : asksAgain ? ' Everyone is asked to accept it at their next sign-in.' : ' Nobody who accepted the version before it is asked again.';
      notifications.show({ message: `Version ${draft.version} of ${draft.title} published.${when_}` });
      onClose();
    },
    onError: (e) => setError(errorText(e)),
  });
  const tomorrow = dayjs().add(1, 'day').startOf('day').toDate();
  const c = count.data;
  const fromWords = effective ? `From ${effective.toLocaleDateString(undefined, { dateStyle: 'medium' })}, ` : '';
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (!publish.isPending) publish.mutate(); }}>
      <Stack gap="lg">
        <Text size="sm" c="dimmed">{draft.title} <span className="id">{doc.key}</span></Text>
        {first ? (
          <LockedSwitch label="Everyone must accept it" description="Everyone accepts a first version: nobody has accepted this document yet." checked />
        ) : (
          <Switch
            label="Everyone must accept it again"
            description={again ? 'For a change to what the document says. Everyone is asked at their next sign-in, including people who accepted an earlier version.' : 'Off, for a correction that changes nothing, such as a typo. People who accepted an earlier version are not asked again; only new people are.'}
            checked={again}
            onChange={(e) => setAgain(e.currentTarget.checked)}
            data-autofocus
          />
        )}
        <DatePickerInput
          label="In effect from"
          description="Leave it empty for it to take effect when it is published. A date starts at the beginning of that day, your time."
          placeholder="When it is published"
          value={from}
          onChange={setFrom}
          minDate={tomorrow}
          clearable
          valueFormat="MMM D, YYYY"
          classNames={{ input: 'num' }}
          maw={260}
        />
        <Box>
          {c ? (
            <Stack gap={6} role="status">
              <Text size="sm" fw={500} className="num">{fromWords}{peopleFraction(c.asked, c.people)} will be asked to accept it at their next sign-in.</Text>
              {c.covered > 0 && <Text size="sm" c="dimmed" className="num">{c.covered} {c.covered === 1 ? 'is' : 'are'} covered by their organization's custom agreement and {c.covered === 1 ? 'is' : 'are'} not asked.</Text>}
              <Text size="sm" c="dimmed">A person is asked at their next full sign-in, which comes within their organization's sign-in limit (7 days by default). Nobody is asked in the middle of a session.</Text>
            </Stack>
          ) : count.isError ? (
            <Text size="sm" c="danger">How many people it asks could not be counted: {errorText(count.error)}. Publishing still works.</Text>
          ) : (
            <Stack gap={8} aria-hidden="true"><Skeleton height={12} width="80%" /><Skeleton height={12} width="60%" /></Stack>
          )}
        </Box>
        {error && <Text size="sm" c="danger" role="alert">{error}</Text>}
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={publish.isPending}>Cancel</Button>
          <Button type="submit" disabled={publish.isPending}>Publish version {draft.version}</Button>
        </Group>
      </Stack>
    </form>
  );
}

// Every published version, newest first. A row opens its text.
function Versions({ doc, versions, loading }: { doc: LegalDocumentSummary | undefined; versions: LegalVersion[]; loading: boolean }) {
  const [opened, setOpened] = useState<string | null>(null);
  const gridRow = useGridRows(versions.map((v) => v.id));
  const shown = opened ? versions.find((v) => v.id === opened) ?? null : null;
  const pane = useShown(shown);
  return (
    <>
      <Title order={2} fz="lg" mb="md">Versions</Title>
      <Paper p={0} style={{ overflow: 'hidden' }}>
        {!loading && versions.length === 0 ? (
          <EmptyState icon={IconFileOff} title="Nothing published yet" body="Each version is listed here once it is published, with who published it and who had to accept it." />
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table role="grid" layout="fixed" aria-label="Published versions" aria-busy={loading}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={100}>Version</Table.Th><Table.Th w={140}>State</Table.Th><Table.Th w={150}>Effective</Table.Th><Table.Th>Published</Table.Th><Table.Th w={190}>Who must accept it</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading && [0, 1].map((i) => (
                  <Table.Tr key={i} aria-hidden="true" bg="transparent">
                    {[40, 72, 96, 200, 120].map((w, j) => <Table.Td key={j}><Skeleton height={12} width={w} /></Table.Td>)}
                  </Table.Tr>
                ))}
                {versions.map((v) => (
                  <Table.Tr key={v.id} {...gridRow(v.id, () => setOpened(v.id))} onClick={() => setOpened(v.id)} style={{ cursor: 'pointer' }} aria-label={`Version ${v.version}, ${stateOf(v, doc)}`}>
                    <Table.Td className="num" fw={500}>{v.version}</Table.Td>
                    <Table.Td><VersionState version={v} doc={doc} /></Table.Td>
                    <Table.Td><Text size="sm" className="num">{effectiveDay(v)}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm" className="num">{when(v.publishedAt)}</Text>
                      <Text size="xs" c="dimmed">{v.publishedByName || 'MotionSafe'}</Text>
                    </Table.Td>
                    <Table.Td><Text size="sm">{whoAccepts(v)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>
      <PaneDrawer opened={pane.opened} onClose={() => setOpened(null)}>
        {pane.shown && <VersionPane key={pane.shown.id} version={pane.shown} doc={doc} />}
      </PaneDrawer>
    </>
  );
}

// A version is in effect (people read it now), scheduled for a date, or
// replaced by a later one.
function stateOf(v: LegalVersion, doc: LegalDocumentSummary | undefined): string {
  if (doc?.status === 'retired') return 'retired';
  if (doc?.inEffect?.id === v.id) return 'in effect';
  if (doc?.scheduled?.id === v.id) return 'scheduled';
  return 'replaced';
}

// When it took effect: its own date when it was scheduled, else when it was published.
const effectiveDay = (v: LegalVersion) => day(v.effectiveAt ?? v.publishedAt);

// Whether publishing it asked everyone, or only people who had not accepted the version before it.
const whoAccepts = (v: LegalVersion) => (v.requiresReacceptance ? 'Everyone' : 'New people only');

function VersionState({ version, doc }: { version: LegalVersion; doc: LegalDocumentSummary | undefined }) {
  const state = stateOf(version, doc);
  // The same badges the keys page gives a key's state: green for the one in use, amber for the one waiting, gray for the ones before.
  return <Badge color={state === 'in effect' ? 'success' : state === 'scheduled' ? 'warning' : 'gray'}>{state}</Badge>;
}

// One version, as a page of the case file: when and by whom, what it asked,
// what changed, and its text as people read it.
function VersionPane({ version: v, doc }: { version: LegalVersion; doc: LegalDocumentSummary | undefined }) {
  return (
    <>
      <PaneHeader title={`${v.title}, version ${v.version}`} subtitle={<span className="id">{doc?.key}</span>} state={<VersionState version={v} doc={doc} />} />
      <PaneBody>
        <DrawerSection title="Publication">
          <Facts rows={[
            { label: 'Published', value: when(v.publishedAt) },
            { label: 'By', value: v.publishedByName || 'MotionSafe' },
            { label: 'Who must accept it', value: v.requiresReacceptance ? 'Everyone, including people who accepted an earlier version' : 'New people only; people who accepted an earlier version were not asked again' },
            { label: 'Effective', value: effectiveDay(v) },
            { label: 'Text sha256', value: v.bodySha256, mono: true },
          ]} />
        </DrawerSection>
        {v.changeSummary && (
          <DrawerSection title="What changed">
            <Text size="sm" style={{ whiteSpace: 'pre-line' }}>{v.changeSummary}</Text>
          </DrawerSection>
        )}
        <DrawerSection title="Text">
          <LegalBlocks blocks={fromWire(v.blocks)} size="sm" titleOrder={3} empty="This version has no text." />
        </DrawerSection>
      </PaneBody>
    </>
  );
}
